/**
 * PWA Update Prompt component.
 * Shows a banner when a new version of the app is available,
 * allowing the user to update or dismiss.
 *
 * @author Milton Filho
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Box, Flex, Text, Button } from 'rebass';

const INTERVAL_MS = 60 * 60 * 1000; // Check for updates every hour
const VERSION_ENDPOINT = '/version.json';

const fetchLatestVersion = async (): Promise<string | null> => {
  try {
    const response = await fetch(`${VERSION_ENDPOINT}?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { version?: string };
    return data.version ?? null;
  } catch {
    return null;
  }
};

export const PwaUpdatePrompt: React.FC = () => {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (registration) {
        setInterval(() => registration.update(), INTERVAL_MS);
      }
    },
    onRegisterError(error) {
      console.error('Service worker registration error:', error);
    },
  });

  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  // When the SW signals that a new build is ready, the running bundle is
  // still the *previous* version, so `__APP_VERSION__` would announce the
  // outdated number. Fetch the freshly-deployed version.json (network-only)
  // so we can show the actual incoming version.
  useEffect(() => {
    if (!needRefresh) return;
    let cancelled = false;
    fetchLatestVersion().then(version => {
      if (!cancelled && version) {
        setLatestVersion(version);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [needRefresh]);

  const handleUpdate = useCallback(() => {
    updateServiceWorker(true);
  }, [updateServiceWorker]);

  const handleDismiss = useCallback(() => {
    setNeedRefresh(false);
  }, [setNeedRefresh]);

  if (!needRefresh) {
    return null;
  }

  const displayVersion = latestVersion ?? __APP_VERSION__;

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        bg: 'rgb(0, 35, 78)',
        color: 'white',
        px: 3,
        py: 3,
        boxShadow: '0 -2px 10px rgba(0, 0, 0, 0.2)',
      }}>
      <Flex
        alignItems="center"
        justifyContent="space-between"
        sx={{ maxWidth: 600, mx: 'auto', gap: 3 }}
        flexWrap="wrap">
        <Box sx={{ flex: '1 1 auto', minWidth: 200 }}>
          <Text fontWeight="bold" fontSize={2}>
            New version available
          </Text>
          <Text fontSize={1} sx={{ opacity: 0.85, mt: 1 }}>
            Version {displayVersion} is ready. Update to get the latest
            features and fixes.
          </Text>
        </Box>
        <Flex sx={{ gap: 2, flexShrink: 0 }}>
          <Button
            onClick={handleDismiss}
            sx={{
              bg: 'transparent',
              border: '1px solid rgba(255, 255, 255, 0.4)',
              color: 'white',
              cursor: 'pointer',
              px: 3,
              py: 2,
              fontSize: 1,
              borderRadius: 4,
              '&:hover': {
                bg: 'rgba(255, 255, 255, 0.1)',
              },
            }}>
            Later
          </Button>
          <Button
            onClick={handleUpdate}
            sx={{
              bg: 'rgb(246, 31, 126)',
              color: 'white',
              cursor: 'pointer',
              border: 'none',
              px: 3,
              py: 2,
              fontSize: 1,
              fontWeight: 'bold',
              borderRadius: 4,
              '&:hover': {
                bg: 'rgb(220, 25, 110)',
              },
            }}>
            Update now
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};
